import { useState, type ReactNode } from "react";
import type { AgentAction } from "@/lib/agent-agui";
import { useAgentStore } from "@/stores/agent-store";
import { Button } from "@/components/design/Button";
import type { ButtonSize, ButtonVariant } from "@/components/design/button-classes";
import { SmikeGlyph } from "./SmikeGlyph";

// The one affordance for a seeded assistant hand-off — reuse it everywhere
// instead of hand-rolling another smike + Button.
//
// The smike mascot is rendered through `currentColor` (a mask over bg-current),
// so it inverts WITH the button rather than vanishing: dark at rest, white on
// the secondary button's black hover fill. smike.svg itself is a fixed-black
// asset, so a plain <img> would disappear on the inverted state.
export function AskAssistantButton({
  id,
  prompt,
  hiddenContext,
  action,
  onBeforeAsk,
  onAsk,
  label,
  disabled,
  variant = "secondary",
  size = "compact",
  className = "",
  title,
  keyShortcuts,
}: {
  id?: string;
  /**
   * The seeded prompt, or a factory called on click (e.g. to vary the phrasing each time). Without one the
   * button only opens the chat, for a page whose context rides along anyway (the Studio notebook).
   */
  prompt?: string | (() => string);
  /**
   * Payload the model needs but the user should not read in their own bubble — a catalogue, a brief.
   * Sent appended to `prompt`, hidden at render (see AGENT_CONTEXT_MARKER).
   */
  hiddenContext?: string | (() => string);
  /**
   * Typed turn intent forwarded out-of-band (`forwardedProps.action`), so the agent-service branches
   * on a field rather than on the prompt's wording. Not part of the message text.
   */
  action?: AgentAction;
  /**
   * Run before the intent is sent — the seam for flushing pending writes so the agent reads the
   * same server state the user is looking at. **A rejection cancels the ask**: nothing is sent, and
   * the surface that owns the failed write shows it (the match editor's SaveStatus bar).
   */
  onBeforeAsk?: () => Promise<unknown>;
  // Takes the click instead of the drawer, for a surface that runs the turn and shows it itself (the Studio
  // notebook). The button keeps its look, so every hand-off to the assistant still reads the same.
  onAsk?: () => void;
  label: ReactNode;
  disabled?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  // For a surface that also triggers it from the keyboard (the Studio composer): the tooltip names the keys,
  // `aria-keyshortcuts` tells a screen reader.
  title?: string;
  keyShortcuts?: string;
}) {
  const ask = useAgentStore((s) => s.ask);
  const openChat = useAgentStore((s) => s.openChat);
  const open = useAgentStore((s) => s.open);
  // `onBeforeAsk` turned the click into an await of a network round-trip, so without this the
  // control stays live for its whole duration: an impatient second click queues a SECOND full LLM
  // turn (the drawer clears `pendingSend` before `handleSend` sets `pending`, so the second queue
  // survives the `!pending` guard and drains when the first turn ends). State, not a ref, so the
  // same flag also renders the button inert — during a slow flush the click would otherwise look
  // like nothing happened at all.
  const [busy, setBusy] = useState(false);
  return (
    <Button
      id={id}
      variant={variant}
      size={size}
      disabled={disabled || busy}
      title={title}
      aria-keyshortcuts={keyShortcuts}
      onClick={async () => {
        if (busy) return;
        // A failed flush must send NO intent: asking anyway would have the assistant propose against
        // a design the server never received, and its write would then land on top of the edit that
        // failed to save. The editor already renders the failure (SaveStatus "Nicht gespeichert ·
        // Erneut versuchen"), so swallowing here adds no second error surface.
        if (onBeforeAsk) {
          setBusy(true);
          try {
            await onBeforeAsk();
          } catch {
            return;
          } finally {
            setBusy(false);
          }
        }
        if (onAsk) {
          onAsk();
          return;
        }
        if (prompt === undefined) {
          openChat();
          return;
        }
        const context = typeof hiddenContext === "function" ? hiddenContext() : hiddenContext;
        ask(typeof prompt === "function" ? prompt() : prompt, { hiddenContext: context, action });
      }}
      className={`gap-1.5 ${className}`}
    >
      {/* Turn the mascot's head to face the chat while the assistant panel is open. */}
      {/* `-my-1` keeps the 20px mascot from driving the control height: the size scale in
          button-classes is padding-based around a ~16px text line box, so an unclipped 20px glyph
          made every AskAssistantButton 4px taller than the plain Button beside it. The negative
          margin trims its margin box back under the line box; the mascot still renders at 20px. */}
      <SmikeGlyph
        className={`-my-1 -ml-0.5 h-5 w-5 transition-transform duration-200 motion-reduce:transition-none ${
          open ? "-scale-x-100" : ""
        }`}
      />
      {label}
    </Button>
  );
}
